import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { registerDoctor } from '../../../redux/middleware/user/doctor/register';
import { Box, Button, Checkbox, CircularProgress, Grid, TextField, Typography, Alert, InputAdornment, IconButton, MenuItem, Select, FormControlLabel, Snackbar, Modal, FormControl, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { doctorGoogleRegister } from '../../../redux/middleware/user/doctor/googleAuth';
import GoogleIcon from '@mui/icons-material/Google';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { openModal, closeModal } from '../../../redux/middleware/user/doctor/modal';
import { doctorResendVerificationEmail } from '../../../redux/middleware/user/doctor/verification';
import { Link } from 'react-router-dom';
import { shades } from "../../../../theme.js";
import image1  from './images/image33.avif';
import CaptchaSection from './captchaSection';

const DoctorRegister = () => {
  const dispatch = useDispatch();
  const specialties = ["Cardiology", "Dermatology", "Neurology", "Pediatrics", "General Medicine"];
  const { isModalOpen } = useSelector((state) => state.modal);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [ register, setRegister ]  = useState('Register');
  const [inputValue, setInputValue] = useState('');
  const client_id = process.env.GOOGLE_CLIENT_ID;
  const navigate = useNavigate();
  // Handle backed errors
  const [backendErrors, setBackendErrors] = useState({});
  // State for error modal
  const [errorModal, setErrorModal] = useState({
    open: false,
    message: '',
    field: ''
  });

const [formData, setFormData] = useState({
  title: "",
  full_name: "",
  gender: "",
  email: "",
  phone_number: "",
  address: "",
  license_number: "",
  speciality: "",
  password: "",
  confirm_password: "",
  agree_to_terms: false,
  username: "",
});

  // Add reCAPTCHA state
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const recaptchaRef = useRef(null);

  // Validate form data for errors
  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.full_name) newErrors.full_name = "Please enter a valid doctor name";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) newErrors.email = "Please enter a valid email";
    if (!formData.phone_number || formData.phone_number.length > 15) newErrors.phone_number = "Please enter a valid phone number";
    // if (!formData.description || formData.description.length < 10) newErrors.description = "Please enter a valid description";
    if (!formData.speciality) newErrors.speciality = "Please select a speciality";
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!formData.password || !passwordRegex.test(formData.password)) newErrors.password = "Password must be at least 8 characters, contain at least one uppercase letter, one lowercase letter, one number and one special character";
    if (formData.password !== formData.confirm_password) newErrors.confirm_password = "Passwords do not match";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handlePhoneValueChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, phone_number: value }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  }, []);

  // Add handleRecaptchaChange
  const handleRecaptchaChange = (token) => {
    console.log('DoctorRegister: handleRecaptchaChange called with token:', token);
    setRecaptchaToken(token);
    if (token) {
      setIsCaptchaVerified(true);
      setSuccessMessage('Captcha verified!');
      setCaptchaError(null);
    } else {
      setIsCaptchaVerified(false);
      setSuccessMessage(null);
    }
  };

  // Modify handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setCaptchaError(null);

    if (!recaptchaToken) {
      setCaptchaError('Please complete the reCAPTCHA verification.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setBackendErrors({});
    
    try {
      const registrationPayload = {
        ...formData,
        recaptcha_token: recaptchaToken,
      };
      delete registrationPayload.confirm_password;

      const response = await dispatch(registerDoctor(registrationPayload));
      
      if (response.error || response.fieldErrors) {
        // Handle errors as before
        if (response.fieldErrors) {
          setBackendErrors(response.fieldErrors);
          
          if (response.fieldErrors.username) {
            setErrorModal({
                open: true,
                message: "This username is already taken. Please choose another one.",
                field: 'username'
            });
          } else if (response.fieldErrors.email) {
            setErrorModal({
                open: true,
                message: "This email is already registered. Please use a different email or login to your existing account.",
                field: 'email'
            });
          } else if (response.fieldErrors.phone_number) {
            setErrorModal({
                open: true,
                message: "This phone number is already registered. Please use a different number or login to your existing account.",
                field: 'phone_number'
            });
          }
        } else {
          toast.error(response.error);
        }
        
        // Reset captcha on error
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
        setIsCaptchaVerified(false);
        setSuccessMessage(null);
      } else {
        // Success case
        dispatch(openModal());
        setIsSuccess(true);
        
        // Reset form and captcha
        setFormData({
            title: "",
            full_name: "",
            gender: "",
            email: "",
            phone_number: "",
            address: "",
            license_number: "",
            speciality: "",
            password: "",
            confirm_password: "",
            agree_to_terms: false,
            username: "",
        });
        setRecaptchaToken(null);
        setIsCaptchaVerified(false);
        setSuccessMessage(null);
        recaptchaRef.current?.reset();
        
        toast.success("Registration successful! Please check your email for verification instructions.");
      }
    } catch (error) {
      console.error("Registration failed:", error);
      toast.error("Registration failed. Please try again.");
      
      // Reset captcha on error
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      setIsCaptchaVerified(false);
      setSuccessMessage(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleShowPassword = () => setShowPassword(prev => !prev);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(prev => !prev);

  const handledoctorGoogleRegister = (response) => {
    if (response.error) {
      console.log('Google login failed', response.error);
      return;
    }

    const tokenId = response.tokenId;
    console.log("Google OAuth token:", tokenId);

    //Google OAuth token

    // dispatch(doctorGoogleRegister(tokenId));
    dispatch(doctorGoogleRegister(response.credential));

  };

  const handleOkClick = () => {
    dispatch(closeModal());
  };


// Custom styled components remain the same as before
const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(2),
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  margin: "0 auto",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  boxShadow: "0 8px 32px rgba(31, 38, 135, 0.15)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.18)",

  [theme.breakpoints.up("sm")]: {
    maxWidth: "300px",
  },
  [theme.breakpoints.up("md")]: {
    maxWidth: "400px",
  },

}));

const StyledTextField = {
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderColor: shades.neutral[300],
        },
        "&:hover fieldset": {
            borderColor: shades.neutral[300],
        },
        "&.Mui-focused fieldset": {
            borderColor: shades.neutral[500],
        },
    },
    "& .MuiInputBase-input": {
        color: shades.neutral[900],
    },
    "& .MuiInputLabel-root": {
        color: "darkgray",
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "black",
    },

  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#a8c2d5',
      borderRadius: '8px',
    },
    '&:hover fieldset': {
      borderColor: '#5185ac',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#13334c',
    },
  },
  '& .MuiInputBase-input': {
    color: "black",
    '&:-webkit-autofill': {
      '-webkit-box-shadow': '0 0 0 100px white inset',
      color: "black",
      color: "black",
    },
    p: 2
  },
  '& .MuiInputLabel-root': {
    color: '#13334c',
  },
};

const StyledButton = {
  background: 'linear-gradient(45deg, #13334c 30%, #5185ac 90%)',
  borderRadius: '8px',
  border: 0,
  color: 'white',
  height: 48,
  padding: '0 30px',
  boxShadow: '0 3px 5px 2px rgba(19, 51, 76, .3)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    background: 'linear-gradient(45deg, #0f2b40 30%, #456d8f 90%)',
    transform: 'scale(1.02)',
  },
};

const StyledSelect = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'white',
    '& fieldset': {
      borderColor: '#a8c2d5',
    },
    '&:hover fieldset': {
      borderColor: '#5185ac',
    },
    '&.Mui-focused': {
      backgroundColor: 'linear-gradient(45deg, #0f2b40 30%, #456d8f 90%)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#13334c',
    },
  },
  '& .MuiSelect-select': {
    padding: '14px',
  },
  '& .Mui-focused .MuiSelect-select': {
    color: '#13334c',
  },
};


return (
  // Outer most div
  <>
  {/* Error Modal */}
  <Modal
      open={errorModal.open}
      onClose={() => setErrorModal({ ...errorModal, open: false })}
      aria-labelledby="error-modal-title"
      aria-describedby="error-modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
      }}>
        <Typography id="error-modal-title" variant="h6" component="h2" gutterBottom>
          Registration Error
        </Typography>
        <Typography id="error-modal-description" sx={{ mt: 2 }}>
          {errorModal.message}
        </Typography>
        <Button 
          onClick={() => setErrorModal({ ...errorModal, open: false })}
          sx={{ 
            mt: 3,
            background: 'linear-gradient(45deg, #13334c 30%, #5185ac 90%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, #0f2b40 30%, #456d8f 90%)',
            }
          }}
          variant="contained"
        >
          OK
        </Button>
      </Box>
    </Modal>
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100%',
        bgcolor: shades.neutral[100],
        pl: { md: "5%" },
        pr: { md: "5%" },
        pt: { md: "2.5%" },
        pb: { md: "2.5%" },
      }}
    >
        {/* First div with form */}
        <Box
          sx={{
            boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
            width: { xs: '100%', md: '50%' },
            overflow: "auto",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            borderRadius: "8px",
          }}
          >
          <Typography
              variant="h3"
              fontWeight="bold"
              gutterBottom
              align="center"
              sx={{ mt: { xs: 8, md: 2}, mb: {xs : 8, md: 4}, color: shades.neutral[900], backgroundColor: 'white' }}
            >
              Doctor Registration
            </Typography>

          <Box component="form" onSubmit={handleSubmit}
            sx={{ backgroundColor: "white",
                  width: "100%",
                  p: { xs: 2, md: 12 },
                }}
            >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={formData.title || ""}
                  onChange={handleChange}
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                  sx={StyledTextField}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username || ""}
                  onChange={handleChange}
                  error={!!errors.username}
                  helperText={errors.username}
                  sx={StyledTextField}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="full_name"
                  value={formData.full_name || ""}
                  onChange={handleChange}
                  error={!!errors.full_name}
                  helperText={errors.full_name}
                  sx={StyledTextField}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  sx={StyledTextField}
                />
              </Grid>

              <Grid item xs={12}>
                <PhoneInput
                  value={formData.phone_number || ""}
                  onChange={handlePhoneValueChange}
                  defaultCountry="us"
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    border: '1px solid #a8c2d5'
                  }}
                  sx={StyledTextField}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="License Number"
                  name="license_number"
                  value={formData.license_number || ""}
                  onChange={handleChange}
                  error={!!errors.license_number}
                  helperText={errors.license_number}
                  required
                  sx={StyledTextField}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Select
                    name="speciality"
                    value={formData.speciality || ""}
                    onChange={handleChange}
                    required
                    displayEmpty
                    sx={{
                      border: "1px solid #a8c2d5",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      color: "#13334c",
                      "&:focus": {
                        backgroundColor: "#f0f0f0",
                        color: "#13334c",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#f0f0f0",
                        color: "#13334c",
                      },
                    }}
                  >
                    <MenuItem
                      value=""
                      disabled
                      sx={{
                        color: "#13334c",
                      }}
                    >
                      <em>Select Specialty</em>
                    </MenuItem>
                    {specialties.map((s, i) => (
                      <MenuItem
                        key={i}
                        value={s}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#f0f0f0",
                            color: "white",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#e0e0e0",
                          },
                        }}
                      >
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password || ""}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  required
                  sx={StyledTextField}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowPassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirm_password || ""}
                  onChange={handleChange}
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password}
                  required
                  sx={StyledTextField}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowConfirmPassword} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.agree_to_terms}
                      onChange={(e) =>
                        setFormData({ ...formData, agree_to_terms: e.target.checked })
                      }
                      sx={{ color: shades.neutral[900] }}
                    />
                  }
                  sx={{ color: shades.neutral[900] }}
                  label="I agree to the terms and conditions"
                />
              </Grid>

              {/* Add CaptchaSection */}
              {recaptchaSiteKey ? (
                <Grid item xs={12}>
                  <CaptchaSection
                    loading={false}
                    error={captchaError}
                    onRecaptchaChange={handleRecaptchaChange}
                    successMessage={successMessage}
                    isCaptchaVerified={isCaptchaVerified}
                    sitekey={recaptchaSiteKey}
                    recaptchaRef={recaptchaRef}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Typography color="error">reCAPTCHA Site Key not configured.</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSubmitting || !isCaptchaVerified}
                  sx={StyledButton}
                >
                  {isSubmitting ? <CircularProgress size={24} /> : 'Register'}
                </Button>
              </Grid>
            </Grid>
          <Divider sx={{ my: 3, color: shades.neutral[900] }}>Or</Divider>

          <GoogleOAuthProvider clientId={client_id}>
            <GoogleLogin
              buttonText="Register with Google"
              onSuccess={handledoctorGoogleRegister}
              sx={StyledTextField}
              onFailure={(error) => console.error('Google Login Error:', error)}
              render={(renderProps) => (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<GoogleIcon />}
                  onClick={renderProps.onClick}
                  // sx={{ mt: 2, color: shades.neutral[900] }}
                  sx={{StyledButton}} // Custom styled button
                >
                  Register with Google
                </Button>
              )}
            />
          </GoogleOAuthProvider>

          <Typography
            variant="body2"
            align="center"
            sx={{ mt: 3, color: shades.neutral[900] }}
          >
            Already have an account?{' '}
            <Link to="/app/doctor/login">
              Login here
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* Second div with image */}
      <Box
        sx={{
          boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
          overflow: "hidden",
          display: { xs: 'none', md: 'block' },
          width: '50%',
          bgcolor: 'white',
        }}
      >
      <img src={image1} style={{ height: '100%', width: '100%' }} />
      </Box>
    </Box>
    </>
    );
  }
export default DoctorRegister;
